/**
 * Analytics gate — decides whether PostHog should capture events for
 * the currently-logged-in user, and identifies real users so we can
 * segment.
 *
 * Rules:
 *   • Never opt back in a user who has explicitly opted out via
 *     ?ph_opt_out=1 — the localStorage flag stays sticky.
 *   • Lifetime-free (admin / staff) accounts get auto opted-out so our
 *     own dogfooding doesn't show up in traffic.
 *   • Everyone else — identify by user id so PostHog attributes events
 *     to a stable person instead of the anonymous device id.
 *
 * Called from AuthContext after a successful session load.
 */
export function applyAnalyticsGate(user) {
  if (typeof window === "undefined" || !window.posthog) return;

  // Bot filter / explicit opt-out already ran in index.html before
  // posthog.init — nothing more to do.
  if (window.__HH_ANALYTICS_DISABLED__) return;

  // Bot filter / explicit opt-out already ran in index.html before
  // posthog.init — nothing more to do.
  if (window.__HH_ANALYTICS_DISABLED__) return;

  // Sticky user-level opt-out set previously? Respect it.
  try {
    if (localStorage.getItem("ph_opt_out") === "1") {
      window.posthog.opt_out_capturing && window.posthog.opt_out_capturing();
      return;
    }
  } catch (_) { /* private mode */ }

  if (!user || !user.id) {
    // Anonymous / signed-out — nothing to identify.
    return;
  }

  // Auto opt-out lifetime-free (team + tester) accounts.
  if (user.lifetime_free) {
    try { localStorage.setItem("ph_opt_out", "1"); } catch (_) { /* noop */ }
    window.posthog.opt_out_capturing && window.posthog.opt_out_capturing();
    return;
  }

  // Real user — make sure we're opted in and identify them so retention
  // and funnels have stable people instead of device blobs.
  try {
    window.posthog.opt_in_capturing && window.posthog.opt_in_capturing();
    window.posthog.identify(user.id, {
      email: user.email,
      name: user.name,
      household_id: user.household_id,
      role: user.role,
      subscription_status: user.subscription_status,
      subscription_plan: user.subscription_plan,
    });
  } catch (e) {
    // Analytics must never break the app.
    console.debug("Analytics identify failed:", e);
  }
}
